// src/services/materialDidactico.service.ts

import {
  collection, doc, addDoc, updateDoc, deleteDoc,
  onSnapshot, query, orderBy, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/config/firebase';

export interface Diapositiva {
  id: string;
  imageUrl: string;
  storagePath: string;
  title?: string;
  department?: string;
  topic?: string;
  order: number;
  createdAt?: Timestamp;
  createdBy?: string;
}

const COL = 'material-didactico';

export const materialDidacticoService = {
  subscribe(cb: (slides: Diapositiva[]) => void): () => void {
    const q = query(collection(db, COL), orderBy('order', 'asc'));
    return onSnapshot(q, (snap) => {
      cb(snap.docs.map((d) => ({ id: d.id, ...d.data() } as Diapositiva)));
    });
  },

  async uploadImage(
    file: File,
    onProgress?: (pct: number) => void
  ): Promise<{ url: string; storagePath: string }> {
    const ext = file.name.split('.').pop();
    const storagePath = `material-didactico/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
    const storageRef = ref(storage, storagePath);
    const task = uploadBytesResumable(storageRef, file);

    return new Promise((resolve, reject) => {
      task.on(
        'state_changed',
        (snap) => onProgress?.(Math.round((snap.bytesTransferred / snap.totalBytes) * 100)),
        reject,
        async () => {
          const url = await getDownloadURL(task.snapshot.ref);
          resolve({ url, storagePath });
        }
      );
    });
  },

  async add(data: Omit<Diapositiva, 'id'>): Promise<string> {
    const ref = await addDoc(collection(db, COL), {
      ...data,
      createdAt: serverTimestamp(),
    });
    return ref.id;
  },

  async update(id: string, data: Partial<Omit<Diapositiva, 'id'>>): Promise<void> {
    await updateDoc(doc(db, COL, id), data);
  },

  async delete(id: string, storagePath: string): Promise<void> {
    await deleteDoc(doc(db, COL, id));
    try {
      await deleteObject(ref(storage, storagePath));
    } catch {
      // ignore if already deleted
    }
  },
};
